import { ContentBox } from 'app/agriculture/components/ContentBox/ContentBox';
import { SimpleGrid } from 'app/agriculture/components/SimpleGrid/SimpleGrid';
import { StatDisplay } from 'app/agriculture/components/StatDisplay/StatDisplay';
import { DiagnosticSnapshot } from 'app/agriculture/queries/devices/types/DiagnosticSnapshot';
import { formatDuration, intervalToDuration, sub } from 'date-fns';
import { StatValue } from 'app/agriculture/components/StatDisplay/StatValue/StatValue';
import { useSnapshot } from 'app/agriculture/queries/snapshots/useSnapshot';
import { useContext } from 'react';
import { DeviceDetailContext } from 'app/agriculture/routes/Devices/DeviceDetails/context/DeviceDetailContextProvider';
import { SuspenseWrapper } from 'app/agriculture/components/SuspenseWrapper/SuspenseWrapper';
import { DiagnosticDisplaySkeleton } from 'app/agriculture/routes/Devices/DeviceDetails/components/display/DiagnosticDisplaySkeleton';

export function DiagnosticDisplay() {
    return (
        <SuspenseWrapper loadingComponent={<DiagnosticDisplaySkeleton />}>
            <WrappedDiagnosticDisplay />
        </SuspenseWrapper>
    );
}

function WrappedDiagnosticDisplay() {
    const { device } = useContext(DeviceDetailContext);
    const {
        timestamp,
        errorCodes,
        memoryFree,
        memoryTotal,
        firmwareVersion,
        hardwareVersion,
        supportedBindingAndModes,
        currentTime,
    } =
        useSnapshot({
            snapshotType: 'Diagnostic',
            parentId: device.id,
            shelfLife: { hours: 1 },
        }) ?? {};
    return (
        <ContentBox.Section heading="Device Stats" disableDivider timestamp={timestamp}>
            <SimpleGrid className="DiagnosticDisplay" columns={3}>
                <StatDisplay
                    name="Error codes"
                    value={!errorCodes ? null : <DeviceErrorCodes errorCodes={errorCodes} />}
                />
                <StatDisplay
                    name="Available memory"
                    value={
                        memoryFree == null || memoryTotal == null ? null : (
                            <>
                                <StatValue unit="kb">{memoryFree}</StatValue> /
                                <StatValue unit="kb">{memoryTotal}</StatValue> (
                                <StatValue unit="%">
                                    {((memoryFree / memoryTotal) * 100).toFixed(0)}
                                </StatValue>
                                )
                            </>
                        )
                    }
                />
                <StatDisplay name="Firmware version" value={firmwareVersion} />
                <StatDisplay name="Hardware version" value={hardwareVersion} />
                <StatDisplay name="Supporting binding and modes" value={supportedBindingAndModes} />
                <StatDisplay name="Uptime" value={calculateUptime({ currentTime, timestamp })} />
            </SimpleGrid>
        </ContentBox.Section>
    );
}

function calculateUptime({
    currentTime,
    timestamp,
}: Pick<Partial<DiagnosticSnapshot>, 'currentTime' | 'timestamp'>) {
    if (currentTime == null || !timestamp) return null;

    return formatDuration(
        intervalToDuration({
            start: sub(timestamp, { seconds: currentTime }),
            end: timestamp,
        }),
    );
}

function DeviceErrorCodes({ errorCodes }: Pick<Partial<DiagnosticSnapshot>, 'errorCodes'>) {
    if (!errorCodes) return null;

    return (
        <div className="DeviceErrorCodes">
            {errorCodes.map((code) => (
                <h5 key={code}>{errorCodeToString(code)}</h5>
            ))}
        </div>
    );
}

function errorCodeToString(code: number): string {
    switch (code) {
        case 0:
            return 'No error';
        case 1:
            return 'Low battery power';
        case 2:
            return 'External power supply off';
        case 3:
            return 'GPS module failure';
        case 4:
            return 'Low received signal strength';
        case 5:
            return 'Out of memory';
        case 6:
            return 'SMS failure';
        case 7:
            return 'IP connectivity failure';
        case 8:
            return 'Peripheral malfunction';
        default:
            return code.toString();
    }
}
